// Port of references/claude-design-source/project/src/chrome.jsx.
// SyncStatus + Sidebar + TopBar + NavigationProgress. Navigation drives via
// TanStack Router (instead of view state). RBAC gating reuses canAtLeast()
// from auth/.

import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { clearPrivateQueryCache } from '@/cache/queryClient';
import {
  notifySessionEnded,
  refreshAllData,
  useDataRevisionCoordinator,
  useGlobalRefreshState,
} from '@/cache/data-revision-coordinator';
import { canAtLeast } from '@/auth/capabilities';
import { useMe } from '@/auth/useMe';
import { useCopilotStatus, useHealthSummary, useSystemHealth, useAlerts } from '@/api/hooks';
import { usePendingInvoiceCount } from '@/api/invoicing-hooks';
import { computeChipState, relTime } from '@/lib/chip-state';
import { pillClass } from '@/lib/system-metrics';
import type { Capability, CapabilityLevel, Role } from '@/api/types.gen';
import type { ReactNode } from 'react';
import { Icons } from './primitives';
import { useAdaptive, useMediaQuery, usePhone, useTouch } from '@/lib/responsive';
import { PENDING_MS } from '@/lib/navigation-timing';
import { releaseBuiltAt, releaseLabel } from '@/lib/app-release';

// Below this the sidebar is replaced (phone tabs, tablet rail); from here to
// the desktop breakpoint it collapses to icons so a half-screen window keeps
// every destination.
const NARROW_QUERY = '(max-width: 1023px)';
import { ResponsiveOverlay } from './responsive-overlay';
import { CPSparkle } from './copilot';
import { SyncPill, SyncDot } from './sync-pill';

// Live health chip: mirrors the ALERTS TABLE (active incidents drive the
// tier; acked never colors; component-`down` keeps a belt-and-braces red
// channel; metric bands are informational only). All semantics live in
// computeChipState (unit-tested). The pill is a real button: click goes to
// Backend › Alerts; hover or keyboard focus opens the detail popover.
function SyncStatus({ compact = false }: { compact?: boolean } = {}) {
  const tapDetails = useTouch();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: me } = useMe();
  const canAlerts = me ? canAtLeast(me.role, 'backend_view', 'view') : false;
  const healthQuery = useHealthSummary();
  // Full alert list (not active-only): acked counts and the top-alerts rows
  // need it. Shares its cache with the Backend > Alerts screen.
  const alertsQuery = useAlerts(false, canAlerts);
  // Unified system-metric bands (same 60s cadence); gated on backend_view.
  const metricsQuery = useSystemHealth(canAlerts);

  const state = computeChipState({
    health: healthQuery.data,
    healthError: healthQuery.isError,
    alerts: alertsQuery.data?.alerts ?? [],
    alertsError: alertsQuery.isError,
    alertsEnabled: canAlerts,
    metrics: metricsQuery.data,
    metricsError: metricsQuery.isError,
  });
  const { tier, activeTotal, activeCounts, ackedCount, topAlerts, checks } = state;
  // Honest tiers with the active-incident count, so the chip and the
  // Triggered tab always agree. A count-less critical is the component-down
  // channel (no alert row to count).
  const label =
    tier === 'critical'
      ? activeTotal > 0
        ? `Critical · ${activeTotal}`
        : 'Critical — action needed'
      : tier === 'error'
        ? `Errors · ${activeTotal}`
        : tier === 'warn'
          ? `Attention · ${activeTotal}`
          : tier === 'unknown'
            ? 'Status stale'
            : 'All Systems OK';
  const tierWord =
    tier === 'ok' ? 'healthy' : tier === 'warn' ? 'attention' : tier === 'error' ? 'errors' : tier === 'unknown' ? 'stale' : 'critical';
  // The phone header has room for the tier alone; the active count already
  // sits on the Alerts tab, so repeating it here read as two signals.
  const pillText = compact
    ? tier === 'ok' ? 'OK' : tier === 'unknown' ? 'Stale' : tierWord[0]!.toUpperCase() + tierWord.slice(1)
    : label;
  const severityParts = [
    activeCounts.critical > 0 && `${activeCounts.critical} critical`,
    activeCounts.error > 0 && `${activeCounts.error} ${activeCounts.error === 1 ? 'error' : 'errors'}`,
    activeCounts.warn > 0 && `${activeCounts.warn} warn`,
  ].filter(Boolean) as string[];
  const alertsSummary = state.alertsStale
    ? 'alerts feed unreachable — status stale'
    : activeTotal > 0
      ? `${activeTotal} active — ${severityParts.join(' · ')}${ackedCount > 0 ? ` · ${ackedCount} acked` : ''}`
      : ackedCount > 0
        ? `no active alerts · ${ackedCount} acked`
        : 'no active alerts';
  const goToAlerts = () => {
    setOpen(false);
    void navigate({ to: '/backend/alerts' });
  };
  return (
    <div
      className="sync-pill-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        className={'sync-pill ' + pillClass(tier)}
        aria-label={`System status: ${label}. ${tapDetails ? 'Show status details.' : 'Open alerts.'}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onClick={tapDetails ? () => setOpen(true) : goToAlerts}
      >
        <SyncDot band={tier} />
        <span>{pillText}</span>
      </button>
      {open && (
        <div className="sync-pop" role="dialog" aria-label="System status details">
          <div className="sync-pop-hd">
            {tapDetails && <button className="btn" aria-label="Close status details" onClick={() => setOpen(false)}>×</button>}
            <SyncDot band={tier}/>
            <span style={{ fontWeight: 600 }}>System status — {tierWord}</span>
          </div>
          {canAlerts && (
            <>
              <Link
                to="/backend/alerts"
                preload="intent"
                className="sync-row sync-row-link"
                onClick={() => setOpen(false)}
              >
                <span className={`sync-check ${activeTotal > 0 ? pillClass(state.worstSeverity ?? 'warn') : state.alertsStale ? 'unknown' : 'ok'}`}>
                  {activeTotal > 0 ? '!' : state.alertsStale ? '~' : '✓'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="sync-lbl">Alerts</div>
                  <div className="sync-detail">{alertsSummary}</div>
                </div>
              </Link>
              {topAlerts.map((a) => (
                <Link
                  key={a.id}
                  to="/backend/alerts"
                  preload="intent"
                  className="sync-row sync-row-link"
                  onClick={() => setOpen(false)}
                >
                  <SyncDot band={a.severity === 'critical' ? 'critical' : a.severity === 'error' ? 'error' : 'warn'} style={{ flexShrink: 0 }}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="sync-lbl" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                  </div>
                  <div className="sync-stamp">{a.lastSeenMs > 0 ? relTime(a.lastSeenMs) : '—'}</div>
                </Link>
              ))}
            </>
          )}
          {checks.length > 0 && (
            <div className="sync-pop-sub">Signals — informational</div>
          )}
          {checks.map((c) => {
            const body = (
              <>
                <span className={`sync-check ${c.band === 'unknown' ? 'unknown' : c.ok ? 'ok' : 'bad'}`}>{c.band === 'unknown' ? '~' : c.ok ? '✓' : '✕'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="sync-lbl">{c.lbl}</div>
                  <div className="sync-detail">{c.detail}</div>
                </div>
                <div className="sync-stamp">{c.stamp}</div>
              </>
            );
            // Only link when the role can actually reach the target screen —
            // e.g. an error-rate row deep-links to /admin/logs, which a
            // backend_view-only planner cannot open. Otherwise render inert.
            const canGo = !!c.link && !!me && canReachRoute(me.role, c.link, me.productions == null);
            return canGo ? (
              <Link
                key={c.lbl}
                to={c.link!}
                preload="intent"
                className="sync-row sync-row-link"
                onClick={() => setOpen(false)}
              >
                {body}
              </Link>
            ) : (
              <div className="sync-row" key={c.lbl}>
                {body}
              </div>
            );
          })}
          <div className="sync-foot">Auto-refreshing every 30s</div>
        </div>
      )}
    </div>
  );
}

interface NavItem {
  id: string;
  route: string;
  label: string;
  icon: ReactNode;
  capability: Capability;
  min?: CapabilityLevel;
  /** Match only this path, rather than treating path-prefix siblings as children. */
  exact?: boolean;
  /// Also requires an unscoped (global) admin — the API rejects a
  /// production-scoped user on these routes even at the right capability level,
  /// so showing the entry would only lead to a 403.
  globalOnly?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

// Prototype's sidebar `sections` array, with prototype `id` retained for
// reference and `route` added to drive TanStack Router. RBAC `capability` per
// item gates visibility (UX only — the API enforces server-side).
const SECTIONS: NavSection[] = [
  { title: 'Manage', items: [
    { id: 'campaigns',   route: '/campaigns', label: 'Campaigns',         icon: Icons.campaigns, capability: 'campaigns_view' },
    { id: 'audiences',   route: '/audiences', label: 'Audiences',         icon: Icons.audiences, capability: 'audiences_view' },
    { id: 'audience-planner', route: '/audience-planner', label: 'Audience Planner', icon: Icons.search, capability: 'audiences_view' },
    { id: 'creatives',   route: '/creatives', label: 'Creative Library',  icon: Icons.creatives, capability: 'creatives_view' },
  ]},
  { title: 'Reports', items: [
    { id: 'reports',    route: '/reports',           label: 'Performance', icon: Icons.reports, capability: 'reports_view', exact: true },
    { id: 'perf-halo',  route: '/reports/perf-halo', label: 'Perf + Halo', icon: Icons.halo,    capability: 'reports_view' },
    { id: 'delivery',   route: '/reports/delivery',  label: 'Delivery',    icon: Icons.dl,      capability: 'reports_view' },
    { id: 'markup',     route: '/reports/markup',    label: 'Markup',      icon: Icons.dollar,  capability: 'reports_view' },
  ]},
  { title: 'Backend', items: [
    { id: 'productions',  route: '/backend/productions', label: 'Productions',         icon: Icons.productions, capability: 'backend_view' },
    { id: 'events',       route: '/backend/events',      label: 'Events',              icon: Icons.ticket,      capability: 'backend_view' },
    { id: 'venues',       route: '/backend/venues',      label: 'Venues',              icon: Icons.venue,       capability: 'backend_view' },
    { id: 'pixel',        route: '/backend/pixels',      label: 'Website Pixels',      icon: Icons.dl,          capability: 'pixels_view' },
    { id: 'data-pipeline', route: '/backend/data-pipeline', label: 'Data Pipeline',    icon: Icons.flow,        capability: 'backend_view' },
    { id: 'capi-forwarding', route: '/backend/capi-forwarding', label: 'CAPI Forwarding', icon: Icons.send,     capability: 'backend_view' },
    { id: 'pipeline',     route: '/backend/pipeline',    label: 'EncorePro Pipeline',  icon: Icons.pulse,       capability: 'backend_view' },
    { id: 'system-health', route: '/backend/system-health', label: 'System Health',    icon: Icons.pulse,       capability: 'backend_view' },
    { id: 'backend-alerts', route: '/backend/alerts',    label: 'Alerts',              icon: Icons.bell,        capability: 'backend_view' },
  ]},
  { title: 'Admin', items: [
    { id: 'admin-users',  route: '/admin/users',         label: 'Users',               icon: Icons.user,        capability: 'users_manage', min: 'own' },
    { id: 'admin-roles',  route: '/admin/roles',         label: 'Roles',               icon: Icons.shield,      capability: 'roles_manage', min: 'own' },
    { id: 'admin-config', route: '/admin/config',         label: 'Config',              icon: Icons.settings,    capability: 'config_manage', min: 'full', globalOnly: true },
    { id: 'admin-remediations', route: '/admin/remediations', label: 'Remediations',  icon: Icons.refresh, capability: 'config_manage', min: 'full', globalOnly: true },
    // Next to Remediations, and gated identically (the API's
    // `require_drift_access` is `require_remediation_access` restated): drift is
    // the read half of the same job — what changed under us — and Remediations
    // is the write half.
    { id: 'admin-provider-drift', route: '/admin/provider-drift', label: 'Provider drift', icon: Icons.pulse, capability: 'config_manage', min: 'full', globalOnly: true },
    // Monthly Xero invoicing. Admin-only and global: an invoice covers every
    // production (`billing_manage` is the one capability nobody but admin holds).
    { id: 'admin-invoices', route: '/admin/invoices', label: 'Invoices', icon: Icons.dollar, capability: 'billing_manage', min: 'full', globalOnly: true },
    { id: 'admin-logs',   route: '/admin/logs',          label: 'Logs',                icon: Icons.terminal,    capability: 'logs_view',    min: 'own' },
    { id: 'admin-audit',  route: '/admin/audit',         label: 'Audit log',           icon: Icons.history,     capability: 'audit_view',   min: 'own' },
  ]},
];

// Whether a role can open the screen a given route points at, by matching the
// route to its nav item's capability (the same gate the sidebar uses). Unknown
// routes are permitted (fail-open — the route's own guard still applies).
function canReachRoute(role: Role, route: string, isGlobalAdmin = true): boolean {
  for (const s of SECTIONS) {
    for (const it of s.items) {
      if (route === it.route || route.startsWith(`${it.route}/`)) {
        return canAtLeast(role, it.capability, it.min ?? 'view')
          && (!it.globalOnly || isGlobalAdmin);
      }
    }
  }
  return true;
}

// The Daily Briefing section prepends above Backend ONLY while the
// server-side copilot flag is on (CopilotStatus.enabled). No status data ⇒
// no section — no teasers.
const BRIEF_SECTION: NavSection = {
  title: 'Daily Briefing',
  items: [
    { id: 'brief', route: '/brief', label: 'Today’s Briefing', icon: <CPSparkle size={14}/>, capability: 'backend_view' },
  ],
};

export function Sidebar() {
  const adaptive = useAdaptive();
  const narrow = useMediaQuery(NARROW_QUERY);
  const { data: me } = useMe();
  const role = me?.role;
  // Live unacknowledged-alert count for the nav badge — only polled for roles
  // that can see the Alerts panel (else the endpoint 403s). Deliberately
  // different from the topbar chip: badge = active (unacked) alerts needing a
  // human; chip = ANY unresolved incident (active + acked).
  const canAlerts = !!role && canAtLeast(role, 'backend_view', 'view');
  // `productions === null` is "all productions" — the unscoped global admin.
  const isGlobalAdmin = me?.productions == null;
  const alertsData = useAlerts(true, canAlerts).data;
  const activeAlerts = Number(alertsData?.active_count ?? 0);
  // Invoices waiting for a human. Only polled for an unscoped admin (the
  // endpoint 403s everyone else), and lazily — it is a monthly workflow.
  const canBilling = !!role && canAtLeast(role, 'billing_manage', 'full') && isGlobalAdmin;
  const pendingInvoices = usePendingInvoiceCount(canBilling).data?.invoices.length ?? 0;
  // Copilot gate — the single server-side flag; while status is unknown the
  // section simply isn't there (matches the "no teasers" rule).
  const copilotOn = useCopilotStatus().data?.enabled === true;
  if (!role) return null;

  const sections = copilotOn ? [BRIEF_SECTION, ...SECTIONS] : SECTIONS;

  if (narrow) return <AdaptiveNavigation sections={sections.map(section => ({ ...section, items: section.items.filter(it => canAtLeast(role, it.capability, it.min ?? 'view') && (!it.globalOnly || isGlobalAdmin)) }))} activeAlerts={activeAlerts} pendingInvoices={pendingInvoices}/>;
  return (
    <aside className="sidebar" data-collapsed={adaptive ? 'true' : undefined}>
      {sections.map((sec) => {
        const visible = sec.items.filter((it) => canAtLeast(role, it.capability, it.min ?? 'view')
          && (!it.globalOnly || isGlobalAdmin));
        if (visible.length === 0) return null;
        return (
          <Fragment key={sec.title}>
            <div className="side-section">{sec.title}</div>
            {visible.map((n) => (
              <Link
                key={n.id}
                to={n.route}
                className="side-item"
                activeProps={{ className: 'active' }}
                // Sidebar identity is path-only: report tabs and filters live in
                // search params, but do not change which section is selected.
                activeOptions={{ exact: n.exact === true, includeSearch: false }}
                title={adaptive ? n.label : undefined}
              >
                <span className="ico">{n.icon}</span><span className="side-label">{n.label}</span>
                {n.id === 'admin-invoices' && pendingInvoices > 0 && (
                  <span
                    style={{
                      marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px',
                      borderRadius: 9, background: 'var(--warn)', color: 'var(--ink-on-badge)',
                      fontSize: 10.5, fontWeight: 700, display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {pendingInvoices > 99 ? '99+' : pendingInvoices}
                  </span>
                )}
                {n.id === 'backend-alerts' && activeAlerts > 0 && (
                  <span
                    style={{
                      marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px',
                      borderRadius: 9, background: 'var(--bad)', color: 'var(--ink-on-badge)',
                      fontSize: 10.5, fontWeight: 700, display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {activeAlerts > 99 ? '99+' : activeAlerts}
                  </span>
                )}
              </Link>
            ))}
          </Fragment>
        );
      })}
      <div className="side-foot">
        <div className="side-fk">
          {/* The release this tab runs (vite.config.ts): commit count, sha, New York build date. */}
          <span title={releaseBuiltAt(__APP_RELEASE__)}>{releaseLabel(__APP_RELEASE__)}</span>
        </div>
      </div>
    </aside>
  );
}

// Theme toggle. Persists to localStorage; defaults to prefers-color-scheme.
type Theme = 'light' | 'dark';
const THEME_KEY = 'shubert.theme';

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const saved = window.localStorage.getItem(THEME_KEY);
  if (saved === 'dark' || saved === 'light') return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    window.localStorage.setItem(THEME_KEY, theme);
  }, [theme]);
  return [theme, setTheme];
}

// The one-frame acknowledgement of a navigation. The router's `status` flips
// to `pending` the instant a link is clicked — before any loader or
// `beforeLoad` has answered (router-core sets it in `load()` ahead of
// `loadMatches`) — while the pending skeleton (router.ts `defaultPendingMs`)
// waits 150 ms so a fast route never flashes. Hover preloads do not set
// `pending`, so the bar never reacts to a pointer crossing the sidebar.
//
// Rendered by the shell root (routes/__root.tsx), NEVER inside the topbar: the
// phone layout hides the topbar (responsive.css ≤767), and until 2026-09-13
// the bar was its child and went with it, so a phone navigating onto one of
// the search-driven routes — `pendingMs: Infinity`, no skeleton — showed
// nothing at all while the loader ran; the outgoing screen just sat there. The
// bar is `position: fixed` at the top of the viewport (styles.css
// `.nav-progress`), so it is the same element on every layout, and
// `navigation-progress.dom.test.tsx` pins both the placement and the rule.
//
// The bar itself is decorative (`aria-hidden`); the live region beside it is
// the announcement — "Loading…" once a navigation has been pending for
// `PENDING_MS`, empty otherwise — so a wait the skeleton never covers is still
// audible. The threshold is the skeleton's own: router status goes `pending`
// synchronously on every click, warm ones included, and an unconditional
// region would read "Loading…" for a navigation that resolved in a few
// milliseconds. On a section switch past the threshold the skeleton's own
// `role="status"` may announce too; that is the lesser cost. `aria-busy` is
// deliberately NOT on the status element: AT may defer announcing a busy live
// region until busy clears (route-pending.tsx says why).
export function NavigationProgress() {
  const pending = useRouterState({ select: (s) => s.status === 'pending' });
  const [announced, setAnnounced] = useState(false);
  useEffect(() => {
    if (!pending) {
      setAnnounced(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setAnnounced(true), PENDING_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);
  return (
    <>
      <div className="nav-progress" data-active={pending ? 'true' : undefined} aria-hidden="true" />
      <span className="sr-only nav-progress-status" role="status" aria-live="polite">
        {pending && announced ? 'Loading…' : null}
      </span>
    </>
  );
}

export function TopBar() {
  useDataRevisionCoordinator();
  const [theme, setTheme] = useTheme();
  const dark = theme === 'dark';
  const navigate = useNavigate();
  const refreshState = useGlobalRefreshState();
  const refreshing = refreshState.phase === 'checking' || refreshState.phase === 'refreshing';
  const refreshTitle = refreshState.phase === 'partial_error'
    ? `Refresh partially failed: ${refreshState.error ?? 'some active data could not refresh'}`
    : refreshState.phase === 'error'
      ? `Refresh failed: ${refreshState.error ?? 'unknown error'}`
      : refreshState.phase === 'scope_changed'
        ? 'Access changed — reloading securely'
        : refreshing
          ? 'Checking for newer server data'
          : 'Refresh all active data';

  const logout = useRegisteredMutation({
    domains: ['identity_rbac'],
    mutationFn: (_: void) => api.post<void>('/auth/logout'),
    onSuccess: () => notifySessionEnded(),
    onSettled: async () => {
      await clearPrivateQueryCache();
      void navigate({ to: '/login' });
    },
  });

  return (
    <header className="topbar">
      <div className="brand">
        <div className="brand-wm" style={{ color: 'var(--ink)', fontWeight: 900, letterSpacing: '.05em' }}>
          SHUBERT ADS MANAGER
        </div>
      </div>
      <div className="tb-spacer"/>
      <SyncStatus/>
      <button
        className="tb-iconbtn"
        onClick={() => setTheme(dark ? 'light' : 'dark')}
        title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {dark ? Icons.sun : Icons.moon}
      </button>
      <button
        className="tb-iconbtn"
        onClick={() => void refreshAllData().catch(() => undefined)}
        disabled={refreshing || refreshState.phase === 'scope_changed'}
        title={refreshTitle}
        aria-label="Force refresh"
        aria-busy={refreshing}
        style={{
          color: refreshState.phase === 'error'
            ? 'var(--bad)'
            : refreshState.phase === 'partial_error' || refreshState.phase === 'scope_changed'
              ? 'var(--warn)'
              : undefined,
        }}
      >
        <span
          style={{ display: 'inline-flex', animation: refreshing ? 'pl-spin 0.8s linear infinite' : 'none' }}
        >
          {Icons.refresh}
        </span>
      </button>
      {(
        refreshState.phase === 'partial_error'
        || refreshState.phase === 'error'
        || refreshState.phase === 'scope_changed'
      ) && (
        <span
          title={refreshState.error ?? undefined}
          style={{
            color: refreshState.phase === 'error' ? 'var(--bad)' : 'var(--warn)',
            fontSize: 11,
            fontWeight: 650,
            whiteSpace: 'nowrap',
          }}
        >
          {refreshState.phase === 'partial_error'
            ? `Refresh partially failed · ${refreshState.lastAttempt?.failed ?? 'some'} of ${refreshState.lastAttempt?.total ?? 'several'} updates failed · showing prior data`
            : refreshState.phase === 'error'
              ? 'Refresh failed · showing prior data'
              : 'Access changed'}
        </span>
      )}
      <span className="sr-only" role="status" aria-live="polite">
        {refreshState.phase === 'checking'
          ? 'Checking for data updates'
          : refreshState.phase === 'refreshing'
            ? 'Refreshing data'
            : refreshState.phase === 'partial_error'
              ? 'Data refresh partially failed; showing prior data where updates failed'
              : refreshState.phase === 'error'
                ? 'Data refresh failed; showing prior data'
                : 'Data is current'}
      </span>
      <Link className="btn" to="/account" title="Your API keys and connected apps">
        Account
      </Link>
      <button
        className="btn"
        onClick={() => logout.mutate()}
        title="Logout"
      >
        Logout
      </button>
    </header>
  );
}


function AdaptiveNavigation({ sections, activeAlerts, pendingInvoices }: { sections: NavSection[]; activeAlerts: number; pendingInvoices: number }) {
  const [more, setMore] = useState(false);
  const pathname = useRouterState({ select: state => state.location.pathname });
  const items = sections.flatMap(section => section.items);
  const primaryIds = ['campaigns', 'productions', 'reports', 'backend-alerts'];
  const primary = primaryIds.flatMap(id => items.find(item => item.id === id) ?? []);
  const primaryActive = primary.some(item => pathname === item.route || pathname.startsWith(`${item.route}/`));
  return <>
    <PhoneStatus/>
    <nav className="adaptive-nav" aria-label="Primary navigation">
      {primary.map(item => <Link key={item.id} to={item.route} className="adaptive-nav-item" activeProps={{className:'active'}} activeOptions={{includeSearch:false}} onClick={() => setMore(false)}>
        <span className="ico" aria-hidden="true">{item.icon}</span><span>{item.id === 'reports' ? 'Reports' : item.label}</span>
        {item.id === 'backend-alerts' && activeAlerts > 0 && <span className="adaptive-nav-badge" aria-label={`${activeAlerts} active alerts`}>{activeAlerts > 99 ? '99+' : activeAlerts}</span>}
      </Link>)}
      <button className={`adaptive-nav-item ${more || !primaryActive ? 'active' : ''}`} aria-haspopup="dialog" aria-expanded={more} onClick={() => setMore(true)}>
        <span className="ico" aria-hidden="true">{Icons.more}</span><span>More</span>{pendingInvoices > 0 && <span className="adaptive-nav-dot" role="img" aria-label={`${pendingInvoices} invoices awaiting review`}></span>}
      </button>
    </nav>
    {more && <ResponsiveOverlay kind="picker" label="More navigation and account" className="more-panel" onClose={() => setMore(false)}>
      <div className="drawer-hd more-brand">
        <div><h2>Shubert Ads Manager</h2><div className="more-build" title={releaseBuiltAt(__APP_RELEASE__)}>{releaseLabel(__APP_RELEASE__)}</div></div>
        <button className="btn" onClick={() => setMore(false)} aria-label="Close More">Done</button>
      </div>
      <AdaptiveAccount onNavigate={() => setMore(false)}/>
      {sections.map(section => section.items.length > 0 && <section className="more-section" key={section.title}>
        <h3>{section.title}</h3><div className="more-links">{section.items.filter(item => !primaryIds.includes(item.id) || item.id === 'reports').map(item => <Link key={item.id} to={item.route} activeProps={{className:'active'}} activeOptions={{exact:item.exact,includeSearch:false}} onClick={() => setMore(false)}>
          <span className="ico" aria-hidden="true">{item.icon}</span><span>{item.label}{item.id === 'admin-invoices' && pendingInvoices > 0 ? ` (${pendingInvoices})` : ''}</span>
        </Link>)}</div>
      </section>)}
    </ResponsiveOverlay>}
  </>;
}

// On phones the topbar is gone and the page header is the only header, so
// the status pill is rendered into that header, beside the title. Screens whose
// header has not mounted yet (or that have none) get a fixed pill at the top
// right instead, so the status is never missing.
function PhoneStatus() {
  const phone = usePhone();
  const pathname = useRouterState({ select: state => state.location.pathname });
  const [slot, setSlot] = useState<Element | null>(null);
  useEffect(() => {
    if (!phone) { setSlot(null); return; }
    const main = document.querySelector('.main');
    if (!main) return;
    let frame = 0;
    const find = () => {
      frame = 0;
      const next = main.querySelector('.page-hd');
      setSlot(current => (current === next ? current : next));
    };
    find();
    const observer = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(find); });
    observer.observe(main, { childList: true, subtree: true });
    return () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [phone, pathname]);
  if (!phone) return null;
  if (slot) return createPortal(<SyncStatus compact/>, slot);
  return <div className="phone-status-fixed"><SyncStatus compact/></div>;
}

function AdaptiveAccount({ onNavigate }: { onNavigate: () => void }) {
  const { data: me } = useMe();
  const [theme, setTheme] = useTheme();
  const navigate = useNavigate();
  const refresh = useGlobalRefreshState();
  const logout = useRegisteredMutation({ domains:['identity_rbac'], mutationFn: (_: void) => api.post<void>('/auth/logout'),
    onSuccess: () => notifySessionEnded(), onSettled: async () => { await clearPrivateQueryCache(); void navigate({to:'/login'}); } });
  return <div className="more-account"><div className="account-name">{me?.name} · {me?.role}</div>
    <button className="btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>{theme === 'dark' ? Icons.sun : Icons.moon} {theme === 'dark' ? 'Light theme' : 'Dark theme'}</button>
    <button className="btn" onClick={() => void refreshAllData().catch(() => undefined)} disabled={refresh.phase === 'checking' || refresh.phase === 'refreshing'}>{Icons.refresh} Refresh data</button>
    <Link className="btn" to="/account" onClick={onNavigate}>{Icons.key} Account</Link>
    <button className="btn" onClick={() => logout.mutate()} disabled={logout.isPending}>Sign out</button>
    {(refresh.phase === 'error' || refresh.phase === 'partial_error') && <p role="status">Refresh failed. Showing prior data. Try Refresh data again.</p>}
  </div>;
}
